/**
 * deadline-utils — Helpers de cálculo de prazo (vencido / restante)
 *
 * Centraliza a regra usada em listagens (Tarefas, Atividades) para
 * exibir o badge pill de prazo abaixo da data, mantendo o mesmo
 * leiaute em todos os módulos.
 *
 * @version 1.0.0 (2026-05-10) — Extraído de TaskList.tsx para reuso.
 */
import { differenceInMinutes } from 'date-fns';

/** Variantes visuais do pill de prazo. */
export type DeadlineVariant = 'overdue' | 'warning' | 'remaining';

export interface DeadlineInfo {
  /** Texto curto para o pill (ex: "Vencido: 2d", "Restam: 8h"). */
  text: string;
  /** Variante de cor. */
  variant: DeadlineVariant;
}

/**
 * Calcula informação de prazo (vencido/restante) para exibição em badge.
 *
 * Retorna `null` quando:
 *  - Não há data de vencimento (`dueDate` nulo).
 *  - O item está concluído ou cancelado (não faz sentido mostrar prazo).
 *
 * @param dueDate ISO string do vencimento.
 * @param status status do item (qualquer string; concluído/cancelado oculta o pill).
 */
export function getDeadlineInfo(
  dueDate: string | null | undefined,
  status?: string | null,
): DeadlineInfo | null {
  if (!dueDate) return null;
  if (status === 'completed' || status === 'cancelled' || status === 'skipped') return null;

  const now = new Date();
  const due = new Date(dueDate);
  const diffMin = differenceInMinutes(due, now);

  if (diffMin < 0) {
    // Já passou do prazo
    const absDiff = Math.abs(diffMin);
    const days = Math.floor(absDiff / (60 * 24));
    const hours = Math.floor((absDiff % (60 * 24)) / 60);
    const minutes = absDiff % 60;
    return {
      text:
        days > 0
          ? `Vencido: ${days}d`
          : hours > 0
            ? `Vencido: ${hours}h`
            : `Vencido: ${minutes}min`,
      variant: 'overdue',
    };
  }

  // Ainda dentro do prazo
  const days = Math.floor(diffMin / (60 * 24));
  const hours = Math.floor((diffMin % (60 * 24)) / 60);

  if (days < 1) {
    // Urgência: menos de 24h
    return {
      text: hours > 0 ? `Restam: ${hours}h` : `Restam: ${diffMin}min`,
      variant: 'warning',
    };
  }

  return {
    text: `Restam: ${days}d`,
    variant: 'remaining',
  };
}

/** Classes Tailwind para cada variante de pill de prazo. */
export const DEADLINE_PILL_STYLES: Record<DeadlineVariant, string> = {
  overdue: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  warning: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  remaining: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
};
